import type { HomePageHeroData } from "@/lib/homepage";

type PageHeroProps = {
  data?: HomePageHeroData;
  icon?: string;
  miniTitle?: string;
  title?: string;
  description?: string;
  buttonText?: string;
  buttonHref?: string;
  buttonAction?: () => void;
  backgroundImageMobile?: string;
  backgroundImageSmallTablet?: string;
  backgroundImageTablet?: string;
  backgroundImageSmallDesktop?: string;
  backgroundImageDesktop?: string;
};

const DEFAULT_DATA: Required<HomePageHeroData> = {
  icon: "O",
  miniTitle: "Mini Title",
  title: "Title",
  description: "",
  ctaLabel: "Let's get started",
  ctaUrl: "#",
  backgroundImageMobile:
    "https://api.builder.io/api/v1/image/assets/TEMP/48e3382d455e1ff64ca316ada0f11fa4c7292685?width=1728",
  backgroundImageSmallTablet:
    "https://api.builder.io/api/v1/image/assets/TEMP/6a86e252a0ae61231fac78d46cf6dbbc32f99d4e?width=1728",
  backgroundImageTablet:
    "https://api.builder.io/api/v1/image/assets/TEMP/6a86e252a0ae61231fac78d46cf6dbbc32f99d4e?width=1728",
  backgroundImageSmallDesktop:
    "https://api.builder.io/api/v1/image/assets/TEMP/6a86e252a0ae61231fac78d46cf6dbbc32f99d4e?width=1728",
  backgroundImageDesktop:
    "https://api.builder.io/api/v1/image/assets/TEMP/6a86e252a0ae61231fac78d46cf6dbbc32f99d4e?width=1728",
};

export default function PageHero({ data, ...legacy }: PageHeroProps) {
  const resolved = {
    icon: data?.icon || legacy.icon || DEFAULT_DATA.icon,
    miniTitle: data?.miniTitle || legacy.miniTitle || DEFAULT_DATA.miniTitle,
    title: data?.title || legacy.title || DEFAULT_DATA.title,
    description: data?.description || legacy.description || DEFAULT_DATA.description,
    ctaLabel: data?.ctaLabel || legacy.buttonText || DEFAULT_DATA.ctaLabel,
    ctaUrl: data?.ctaUrl || legacy.buttonHref || DEFAULT_DATA.ctaUrl,
    backgroundImageMobile:
      data?.backgroundImageMobile || legacy.backgroundImageMobile || DEFAULT_DATA.backgroundImageMobile,
    backgroundImageSmallTablet:
      data?.backgroundImageSmallTablet ||
      legacy.backgroundImageSmallTablet ||
      DEFAULT_DATA.backgroundImageSmallTablet,
    backgroundImageTablet:
      data?.backgroundImageTablet || legacy.backgroundImageTablet || DEFAULT_DATA.backgroundImageTablet,
    backgroundImageSmallDesktop:
      data?.backgroundImageSmallDesktop ||
      legacy.backgroundImageSmallDesktop ||
      DEFAULT_DATA.backgroundImageSmallDesktop,
    backgroundImageDesktop:
      data?.backgroundImageDesktop || legacy.backgroundImageDesktop || DEFAULT_DATA.backgroundImageDesktop,
  } as const;

  const bgMobile = resolved.backgroundImageMobile;
  const bgSmallTablet = resolved.backgroundImageSmallTablet || bgMobile;
  const bgTablet = resolved.backgroundImageTablet || bgSmallTablet;
  const bgDesktop =
    resolved.backgroundImageDesktop ||
    resolved.backgroundImageSmallDesktop ||
    bgTablet;

  return (
    <section className="relative flex flex-col items-center bg-onplexus-dark overflow-hidden pageHero-bgImg">
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat sm:hidden"
          style={{ backgroundImage: bgMobile ? `url('${bgMobile}')` : undefined }}
        />
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat hidden sm:block md:hidden"
          style={{ backgroundImage: bgSmallTablet ? `url('${bgSmallTablet}')` : undefined }}
        />
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat hidden md:block lg:hidden"
          style={{ backgroundImage: bgTablet ? `url('${bgTablet}')` : undefined }}
        />
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat hidden lg:block"
          style={{ backgroundImage: bgDesktop ? `url('${bgDesktop}')` : undefined }}
        />
      </div>

      {/* Content Container */}
      <div className="relative w-full flex flex-col items-center gap-4 px-4 pb-12 pt-[200px] max-[599px]:px-4 min-[600px]:max-[767px]:px-6 min-[768px]:px-8">
        {/* Mini Title with Icon */}
        {(resolved.icon || resolved.miniTitle) && (
          <div className="flex items-center gap-2">
            {resolved.icon && (
              <div className="flex w-8 h-8 justify-center items-center">
                <span className="text-onplexus-gold-bright onplexus-icon text-[2.8rem] pt-0.5">
                  {resolved.icon}
                </span>
              </div>
            )}
            {resolved.miniTitle && (
              <span className="title-mini font-bold">
                {resolved.miniTitle}
              </span>
            )}
          </div>
        )}

        {/* H1 Title */}
        <h1 className="w-full text-white text-center text-[42px] leading-[48px] font-normal">
          {resolved.title}
        </h1>

        {/* Description */}
        <div className="flex max-w-[550px] px-0 py-2 pb-12 flex-col items-center w-full">
          <p className="w-full text-white/70 text-center text-lg leading-[26px] font-normal">
            {resolved.description}
          </p>
        </div>

        {/* CTA Button */}
        {resolved.ctaUrl ? (
          <a
            href={resolved.ctaUrl}
            className="flex h-[45px] px-6 justify-center items-center gap-1.5 bg-onplexus-gold text-onplexus-dark hover:bg-onplexus-gold-bright rounded-xl pl-6 pr-4 py-3 h-12 font-semibold text-lg cursor-pointer no-underline"
          >
            <span>{resolved.ctaLabel}</span>
            <span className="onplexus-icon text-5xl pt-0.5">→</span>
          </a>
        ) : legacy.buttonAction ? (
          <button
            type="button"
            onClick={legacy.buttonAction}
            className="flex h-[45px] px-6 justify-center items-center gap-1.5 bg-onplexus-gold text-onplexus-dark hover:bg-onplexus-gold-bright rounded-xl pl-6 pr-4 py-3 h-12 font-semibold text-lg cursor-pointer no-underline"
          >
            <span>{legacy.buttonText ?? DEFAULT_DATA.ctaLabel}</span>
            <span className="onplexus-icon text-5xl pt-0.5">→</span>
          </button>
        ) : null}
      </div>
    </section>
  );
}
